import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_ADVANCE_MAX_MONTHS,
  validateAdvanceAmount,
} from './utils/payrollSaudiFormat';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppData } from '../../core/contexts/AppDataContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  ArrowUpTrayIcon as Upload,
  ArrowDownTrayIcon as Download,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  TableCellsIcon as FileSpreadsheet,
  DocumentArrowUpIcon as FileOutput
} from '@heroicons/react/24/outline';
import { Filter, Plus, X, Wallet, CheckCircle, XCircle, User, Settings, FileText } from 'lucide-react';
import { CustomSelect, CustomNumberInput, CustomDatePicker } from '../../shared/components/custom';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import {
  buildSalairesImportExportProps,
  mapSalairesStatCards,
  renderSalairesBulkDeleteFilterAction,
  SalairesListControlsStrip,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useClearSearchOnTableView,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useSalairesConfig } from './useSalairesConfig';

const MONTH_KEYS = ['jan_2026', 'feb_2026', 'mar_2026', 'apr_2026', 'may_2026', 'jun_2026'];

const AvancesPretPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.avancesPretPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const REASON_OPTIONS = [
    { value: 'family_emergency', label: p('reason_family_emergency') },
    { value: 'medical_emergency', label: p('reason_medical_emergency') },
    { value: 'property_purchase', label: p('reason_property_purchase') },
    { value: 'vehicle_purchase', label: p('reason_vehicle_purchase') },
    { value: 'school_fees', label: p('reason_school_fees') },
    { value: 'other', label: p('reason_other') },
  ];

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return p('not_available');
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('not_available');
    return formatBrandMoney(n);
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('not_available'));
  const { settings } = useAppData();
  const salariesConfig = useSalairesConfig();
  const [activeTab, setActiveTab] = useState('avances');
  const entityLabel = () => (activeTab === 'avances'
    ? p('entity_advance', { defaultValue: 'سلفة' })
    : p('entity_loan', { defaultValue: 'قرض' }));
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    reference: '',
    employeeName: '',
    employeeId: '',
    date: '',
    montant: '',
    nbEcheances: '',
    tauxInteret: '',
    motif: '',
    commentaire: ''
  });
  
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Real data from API
  const [avances, setAvances] = useState([]);
  const [prets, setPrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { employees } = useSalairesEmployeeOptions();
  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();

  // Fetch data from API
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/salaries/advances-loans');
      const allData = sortRecordsNewestFirst(Array.isArray(response) ? response : (response?.data || []));
      setAvances(allData.filter((item) => item.type === 'advance'));
      setPrets(allData.filter((item) => item.type === 'loan'));
    } catch (error) {
      console.error('Error fetching advances/loans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate GLOBAL stats (combined for both tabs)
  const statsGlobal = {
    totalAvances: avances.length,
    totalPrets: prets.length,
    totalActifs: avances.filter(a => a.status === 'active').length + prets.filter(p => p.status === 'active').length,
    totalSoldes: avances.filter(a => a.status === 'paid_off').length + prets.filter(p => p.status === 'paid_off').length,
    montantTotal: avances.reduce((sum, a) => sum + (a.amount || 0), 0) + prets.reduce((sum, p) => sum + (p.amount || 0), 0),
    montantRestant: avances.reduce((sum, a) => sum + (a.remaining_amount || 0), 0) + prets.reduce((sum, p) => sum + (p.remaining_amount || 0), 0)
  };

  const currentData = activeTab === 'avances' ? avances : prets;

  const generateReference = useCallback(() => {
    const prefixKey = activeTab === 'avances' ? 'payrollAdvance' : 'payrollLoan';
    const fallback = activeTab === 'avances'
      ? (salariesConfig.advanceReferencePrefix || 'AVA')
      : (salariesConfig.loanReferencePrefix || 'LOA');
    const prefix = getEntityPrefix({ ...settings, salaries: salariesConfig }, prefixKey, fallback);
    return generateEntityReference(prefix, currentData, (r) => r.reference);
  }, [activeTab, currentData, salariesConfig, settings]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('reference', (row) => row.reference || ''),
      textColumnRule('employee', (row) => `${row.employee_name || ''} ${row.employee_mat || ''}`),
      textColumnRule('date', (row) => fmtDate(row.date)),
      textColumnRule('amount', (row) => String(row.amount ?? '')),
      textColumnRule('remaining', (row) => String(row.remaining_amount ?? '')),
      textColumnRule('installments', (row) => `${row.paid_installments || 0}/${row.total_installments || 0}`),
      textColumnRule('reason', (row) => row.reason || ''),
      exactColumnRule('status', (row) => row.status || ''),
    ],
    [dateLocale, p],
  );

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const filteredData = useMemo(() => {
    const base = currentData.filter((item) => {
      if (
        searchTerm &&
        !item.reference?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !item.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !item.employee_mat?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !item.reason?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [currentData, searchTerm, columnFilters, columnFilterRules]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredData);

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'paid_off', label: p('status_paid_off'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const item = currentData.find((d) => d.id === recordId);
      if (!item) return;
      await localApiAdapter.put(`/salaries/advances-loans/${recordId}`, { ...item, status: newStatus });
      await fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Export handlers
  const handleExportExcel = () => {
    console.log('Export Excel');
  };

  const handleExportPDF = () => {
    console.log('Export PDF');
  };
  
  const resetForm = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormData({
      reference: '',
      employeeName: '',
      employeeId: '',
      date: '',
      montant: '',
      nbEcheances: '',
      tauxInteret: '',
      motif: '',
      commentaire: ''
    });
  };

  const openCreateModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEditingRecord(null);
    setFormData({
      reference: generateReference(),
      employeeName: '',
      employeeId: '',
      date: today,
      montant: '',
      nbEcheances: activeTab === 'avances' ? '1' : '',
      tauxInteret: '',
      motif: '',
      commentaire: ''
    });
    setShowModal(true);
  };
  
  const handleEdit = (item) => {
    setEditingRecord(item);
    setFormData({
      reference: item.reference || '',
      employeeName: item.employee_name || '',
      employeeId: item.employee_id != null ? String(item.employee_id) : '',
      date: item.date || '',
      montant: item.amount || '',
      nbEcheances: item.total_installments || '',
      tauxInteret: item.interest_rate || '',
      motif: item.reason || '',
      commentaire: item.notes || ''
    });
    setShowModal(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!formData.employeeId) {
        notifyError(showNotify, p('alert_select_employee'));
        return;
      }
      if (!formData.date) {
        notifyError(showNotify, p('alert_select_date', { defaultValue: p('form_date') }));
        return;
      }

      const selectedEmployee = employees.find(
        (emp) => String(emp.id) === String(formData.employeeId),
      );
      if (!selectedEmployee) {
        notifyError(showNotify, p('alert_select_employee'));
        return;
      }

      const monthlySalary = selectedEmployee?.salaireBase ?? selectedEmployee?.salary ?? 0;
      const amount = parseFloat(formData.montant);
      if (!Number.isFinite(amount) || amount <= 0) {
        notifyError(showNotify, p('alert_invalid_amount', { defaultValue: p('form_amount') }));
        return;
      }

      const installments = parseInt(formData.nbEcheances, 10);
      if (!Number.isFinite(installments) || installments < 1) {
        notifyError(showNotify, p('alert_invalid_installments', { defaultValue: p('form_installments') }));
        return;
      }

      const advanceCheck = validateAdvanceAmount(amount, monthlySalary);
      if (!advanceCheck.valid && monthlySalary > 0) {
        notifyError(showNotify, p('alert_max_advance', { months: SAUDI_ADVANCE_MAX_MONTHS, max: formatBrandMoney(advanceCheck.maxAmount) }));
        return;
      }

      const reference = (formData.reference || '').trim() || generateReference();

      const payload = {
        type: activeTab === 'avances' ? 'advance' : 'loan',
        reference,
        employee_id: formData.employeeId,
        date: formData.date,
        amount,
        total_installments: installments,
        interest_rate: 0,
        reason: formData.motif,
        notes: formData.commentaire,
      };

      const result = editingRecord
        ? await localApiAdapter.put(`/salaries/advances-loans/${editingRecord.id}`, payload)
        : await localApiAdapter.post('/salaries/advances-loans', payload);

      if (result?.success === false) {
        notifyError(showNotify, result.error || result.message || p('alert_save_failed'));
        return;
      }

      if (editingRecord) notifyUpdated(showNotify, entityLabel());
      else notifyCreated(showNotify, entityLabel());
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving advance/loan:', error);
      notifyError(showNotify, error.message || p('alert_network_error'));
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;

    try {
      const result = await localApiAdapter.delete(`/salaries/advances-loans/${id}`);
      if (result?.success === false) {
        notifyError(showNotify, result.error || p('alert_save_failed'));
        return;
      }
      notifyDeleted(showNotify, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error deleting advance/loan:', error);
      notifyError(showNotify, p('alert_network_error'));
    }
  };

  const listStats = mapSalairesStatCards([
    { key: 'advances', label: p('kpi_total_advances'), value: statsGlobal.totalAvances, icon: BanknotesIcon },
    { key: 'loans', label: p('kpi_total_loans'), value: statsGlobal.totalPrets, icon: CurrencyDollarIcon },
    { key: 'active', label: p('kpi_active'), value: statsGlobal.totalActifs, icon: ClockIcon },
    { key: 'paid', label: p('kpi_paid_off'), value: statsGlobal.totalSoldes, icon: CheckCircleIcon },
  ]);

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm_bulk', { count: selectedRecords.length, defaultValue: p('delete_confirm') }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(
        selectedRecords.map((id) =>
          localApiAdapter.delete(`/salaries/advances-loans/${id}`),
        ),
      );
      clearSelection();
      notifyBulkDeleted(showNotify, deleteCount, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error bulk deleting advances/loans:', error);
      notifyError(showNotify, p('alert_network_error'));
    }
  };

  const importExportProps = buildSalairesImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
    handlers: {
      onExportExcel: handleExportExcel,
      onExportPDF: handleExportPDF,
    },
  });

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'reference', label: p('col_code'), required: true },
    { key: 'employee', label: p('col_employee'), required: true },
    { key: 'date', label: p('col_date') },
    { key: 'amount', label: p('col_amount') },
    { key: 'remaining', label: p('col_remaining') },
    { key: 'installments', label: p('col_installments') },
    { key: 'reason', label: p('col_reason') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: -1 }),
    [qawaemBaseColumns, showCol],
  );

  return (
    <>
    <FacturationListPageLayout
      className="space-y-6"
      containerClassName="employees-container"
      title={activeTab === 'avances' ? p('title_advances') : p('title_loans')}
      subtitle={activeTab === 'avances' ? p('subtitle_advances') : p('subtitle_loans')}
      icon={Wallet}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={renderSalairesBulkDeleteFilterAction({
        selectedCount: selectedRecords.length,
        onBulkDelete: handleBulkDelete,
        t,
      })}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
      onCreate={openCreateModal}
      createLabel={activeTab === 'avances' ? p('add_advance') : p('add_loan')}
      importExport={importExportProps}
      controlsStrip={
        <SalairesListControlsStrip
          hidden={qawaem}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
          isDashboardLightMode={isDashboardLightMode}
          cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
          tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
        />
      }
    >
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 mx-4 mb-4">
        <div className="flex space-x-1">
          <button
            type="button"
            onClick={() => { setActiveTab('avances'); clearSelection(); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'avances'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <BanknotesIcon className="h-5 w-5" />
            <span>{p('tab_advances')}</span>
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('prets'); clearSelection(); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'prets'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <CurrencyDollarIcon className="h-5 w-5" />
            <span>{p('tab_loans')}</span>
          </button>
        </div>
      </div>

      <div className={facturationTableScrollClass(qawaem)}>
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                />
                {showCol('reference') ? <TableColumnHeaderFilter {...colHeaderFilterProps('reference', p('col_code'))} /> : null}
                {showCol('employee') ? <TableColumnHeaderFilter {...colHeaderFilterProps('employee', p('col_employee'))} /> : null}
                {showCol('date') ? <TableColumnHeaderFilter {...colHeaderFilterProps('date', p('col_date'))} /> : null}
                {showCol('amount') ? <TableColumnHeaderFilter {...colHeaderFilterProps('amount', p('col_amount'))} /> : null}
                {showCol('remaining') ? <TableColumnHeaderFilter {...colHeaderFilterProps('remaining', p('col_remaining'))} /> : null}
                {showCol('installments') ? <TableColumnHeaderFilter {...colHeaderFilterProps('installments', p('col_installments'))} /> : null}
                {showCol('reason') ? <TableColumnHeaderFilter {...colHeaderFilterProps('reason', p('col_reason'))} /> : null}
                {showCol('status') ? (
                  <TableColumnHeaderFilter
                    {...colHeaderFilterProps('status', p('col_status'), {
                      align: 'center',
                      kind: 'select',
                      options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                    })}
                  />
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumns}
                  onToggle={handleToggleColumn}
                  visibleCount={visibleColumnCount}
                  totalCount={qawaemColumns.length}
                  thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {loading ? (
                <tr>
                  <td colSpan={visibleTableColCount} className="text-center py-12">
                    <ArrowPathIcon className="h-12 w-12 text-white/30 mx-auto mb-3 animate-spin" />
                    <p className="text-white/50">{p('loading')}</p>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={visibleTableColCount} className="text-center py-12">
                    <InformationCircleIcon className="h-12 w-12 text-white/30 mx-auto mb-3" />
                    <p className="text-white/50 text-lg">{activeTab === 'avances' ? p('empty_advance') : p('empty_loan')}</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(item.id) ? 'bg-white/10' : ''}`}
                  >
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(item.id)}
                      onChange={() => handleSelectRecord(item.id)}
                    />
                    {showCol('reference') ? (
                    <td className="px-6 py-4">
                      <span className="text-white font-semibold">{item.reference || p('not_available')}</span>
                    </td>
                    ) : null}
                    {showCol('employee') ? (
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-white font-semibold">{item.employee_name}</span>
                        <span className="text-white/50 text-xs mt-0.5">{item.employee_mat}</span>
                      </div>
                    </td>
                    ) : null}
                    {showCol('date') ? (
                    <td className="px-6 py-4">
                      <span className="text-white/70 text-sm">{fmtDate(item.date)}</span>
                    </td>
                    ) : null}
                    {showCol('amount') ? (
                    <td className="px-6 py-4">
                      <span className="text-white font-bold text-lg">{formatAmountDisplay((item.amount || 0))}</span>
                    </td>
                    ) : null}
                    {showCol('remaining') ? (
                    <td className="px-6 py-4">
                      <span className={`font-bold text-lg ${item.remaining_amount === 0 ? 'text-green-400' : 'text-orange-400'}`}>
                        {formatAmountDisplay((item.remaining_amount || 0))}
                      </span>
                    </td>
                    ) : null}
                    {showCol('installments') ? (
                    <td className="px-6 py-4">
                      <span className="text-white/70 text-sm">{item.paid_installments || 0} / {item.total_installments}</span>
                    </td>
                    ) : null}
                    {showCol('reason') ? (
                    <td className="px-6 py-4">
                      <span className="text-white/70 text-sm max-w-xs truncate block">{item.reason}</span>
                    </td>
                    ) : null}
                    {showCol('status') ? (
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <QawaemClickableStatusBadge
                          status={item.status}
                          recordId={item.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                    </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onEdit={() => handleEdit(item)}
                        onDelete={() => handleDelete(item.id)}
                        editTitle={p('edit')}
                        deleteTitle={p('delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-white/5 border-t border-white/10">
                <td colSpan={visibleTableColCount} className="px-6 py-4">
                  <div className="flex items-center justify-between text-white/70 text-sm">
                    <span>{activeTab === 'avances' ? p('footer_total_advances', { count: filteredData.length }) : p('footer_total_loans', { count: filteredData.length })}</span>
                    <span>{p('footer_total_amount')} <span className="text-white font-semibold">{formatAmountDisplay(filteredData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}</span></span>
                    <span>{p('footer_total_remaining')} <span className="text-white font-semibold">{formatAmountDisplay(filteredData.reduce((sum, item) => sum + (parseFloat(item.remaining_amount) || 0), 0))}</span></span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
      </div>
    </FacturationListPageLayout>

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`}
            style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingRecord
                  ? (activeTab === 'avances' ? p('modal_edit_advance') : p('modal_edit_loan'))
                  : (activeTab === 'avances' ? p('modal_new_advance') : p('modal_new_loan'))}
              </h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="avance-pret-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button type="button" onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="avance-pret-form" onSubmit={handleSubmit}>
                <div className={SALAIRES_FORM_GRID_CLASS}>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_code')}</label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      className={`w-full border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDashboardLightMode ? 'bg-white border-gray-200 text-gray-900' : 'bg-white/10 border-white/20 text-white'}`}
                      placeholder={p('col_code')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                      <RequiredLabelText text={p('form_employee_name')} required />
                    </label>
                    <CustomSelect
                      value={formData.employeeId}
                      onChange={(value) => {
                        const selectedEmployee = employees.find(
                          (emp) => String(emp.id) === String(value),
                        );
                        setFormData({
                          ...formData,
                          employeeId: value,
                          employeeName: selectedEmployee
                            ? `${selectedEmployee.prenom} ${selectedEmployee.nom}`
                            : '',
                        });
                      }}
                      options={employees.map((emp) => ({
                        value: String(emp.id),
                        label: `${emp.prenom} ${emp.nom} (${emp.mat})`,
                      }))}
                      placeholder={p('placeholder_employee')}
                      autoSelect={false}
                      searchable
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                      <RequiredLabelText text={p('form_date')} required />
                    </label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(value) => setFormData({ ...formData, date: value })}
                      placeholder={p('placeholder_date')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                      <RequiredLabelText text={p('form_amount')} required />
                    </label>
                    <CustomNumberInput
                      value={formData.montant}
                      onChange={(value) => setFormData({ ...formData, montant: value })}
                      placeholder={p('placeholder_amount')}
                      min={0}
                      step={1}
                      decimals={2}
                      required
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                      <RequiredLabelText text={p('form_installments')} required />
                    </label>
                    <CustomNumberInput
                      value={formData.nbEcheances}
                      onChange={(value) => setFormData({ ...formData, nbEcheances: value })}
                      placeholder={p('placeholder_installments')}
                      min={1}
                      step={1}
                      required
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_reason')}</label>
                    <CustomSelect
                      value={formData.motif}
                      onChange={(value) => setFormData({ ...formData, motif: value })}
                      options={REASON_OPTIONS}
                      placeholder={p('placeholder_reason')}
                      autoSelect={false}
                      searchable={true}
                      creatable={true}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                      {p('form_comment')} <span className="text-white/40 text-xs">{p('optional')}</span>
                    </label>
                    <textarea
                      value={formData.commentaire}
                      onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                      rows="3"
                      placeholder={p('placeholder_comment')}
                    />
                  </div>
                  {activeTab === 'prets' && (
                    <div className="md:col-span-3 min-w-0">
                      <p className={`text-sm rounded-xl p-3 border ${isDashboardLightMode ? 'text-gray-600 bg-green-50 border-green-200' : 'text-white/60 bg-green-500/10 border-green-500/20'}`}>
                        {p('form_no_interest_note')}
                      </p>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default AvancesPretPage;


